import { useEffect, useState, useSyncExternalStore } from 'react'
import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ErrorApi } from '@/api/errores'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { CampoTexto } from '@/ui/CampoFormulario'
import { sesionEmpleado, suscribirseSesionEmpleado } from '@/dominio/empleado/sesion'
import { entrarAlPortal } from '@/dominio/empleado/usePortal'
import { Tarjeta } from '../-pantalla'
import { mensajeDeError } from '../-empleado/comun'

/**
 * La entrada al portal: `/empleado/acceso#<invitación>`.
 *
 * **La invitación viaja en el fragmento** (lo que va después de `#`), que el
 * navegador no manda a ningún servidor: no queda en los registros del proxy ni
 * en los del servidor. Al abrir la página se lee y se quita de la dirección,
 * para que tampoco quede en el historial ni se copie al compartir la pantalla.
 *
 * **El empleado confirma quién es con su documento completo.** Cinco
 * equivocados bloquean la invitación: el enlace puede haberse reenviado, y
 * quien lo tenga no pasa de ahí sin la cédula.
 */
export const Route = createFileRoute('/empleado/acceso')({
  component: AccesoEmpleado,
})

const TIPO_DOCUMENTO_NO_COINCIDE = 'https://devtek.do/problemas/documento-no-coincide'
const TIPOS_SIN_REMEDIO = new Set([
  'https://devtek.do/problemas/invitacion-no-valida',
  'https://devtek.do/problemas/invitacion-bloqueada',
  'https://devtek.do/problemas/portal-no-disponible',
])

function leerInvitacion(): string | null {
  try {
    const fragmento = window.location.hash.slice(1).trim()
    return fragmento === '' ? null : decodeURIComponent(fragmento)
  } catch {
    return null
  }
}

function AccesoEmpleado() {
  const sesion = useSyncExternalStore(suscribirseSesionEmpleado, sesionEmpleado, () => null)
  const navegar = useNavigate()
  const [invitacion] = useState(leerInvitacion)
  const [documento, setDocumento] = useState('')
  const [conLetras, setConLetras] = useState(false)
  const [entrando, setEntrando] = useState(false)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    if (window.location.hash !== '') void navegar({ to: '/empleado/acceso', replace: true })
  }, [navegar])

  if (invitacion === null) {
    if (sesion !== null) return <Navigate to="/empleado" replace />

    return (
      <Tarjeta titulo="Entrar al portal del empleado">
        <p className="text-cuerpo text-gris-700">
          Este enlace llegó sin la invitación. Ábralo otra vez desde el mensaje de su empresa, tal como lo recibió; si no funciona, pídale uno
          nuevo.
        </p>
      </Tarjeta>
    )
  }

  const sinRemedio = error instanceof ErrorApi && error.codigo !== undefined && TIPOS_SIN_REMEDIO.has(error.codigo)
  const mensaje = error ? mensajeDeError(error) : null

  const entrar = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setEntrando(true)

    try {
      await entrarAlPortal(invitacion, documento)
      await navegar({ to: '/empleado', replace: true })
    } catch (fallo) {
      setError(fallo)
      if (fallo instanceof ErrorApi && fallo.codigo === TIPO_DOCUMENTO_NO_COINCIDE) setDocumento('')
    } finally {
      setEntrando(false)
    }
  }

  return (
    <Tarjeta titulo="Entrar al portal del empleado">
      {sinRemedio && mensaje ? (
        <Banner tono="rojo" titulo={mensaje.titulo}>
          {mensaje.detalle}
        </Banner>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={(e) => void entrar(e)}>
          <p className="text-cuerpo text-gris-700">
            Su empresa le invitó a ver sus volantes, firmarlos y pedir vacaciones desde aquí. Para confirmar que es usted, escriba su documento
            completo.
          </p>

          <CampoTexto
            etiqueta={conLetras ? 'Su documento' : 'Su cédula'}
            obligatorio
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            inputMode={conLetras ? 'text' : 'numeric'}
            placeholder={conLetras ? 'Pasaporte o NSS' : '001-0000000-0'}
            maxLength={30}
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
            className="h-11 text-[16px]"
          />
          <button type="button" className="self-start text-etiqueta text-marca underline" onClick={() => setConLetras(!conLetras)}>
            {conLetras ? 'Tengo cédula' : 'No tengo cédula: mi documento lleva letras'}
          </button>

          {mensaje ? (
            <Banner tono={error instanceof ErrorApi && error.codigo === TIPO_DOCUMENTO_NO_COINCIDE ? 'ambar' : 'rojo'} titulo={mensaje.titulo}>
              {mensaje.detalle}
            </Banner>
          ) : null}

          <Boton type="submit" variante="primario" ancho disabled={entrando || documento.trim() === ''} className="h-11">
            {entrando ? 'Entrando…' : 'Entrar'}
          </Boton>

          <p className="text-etiqueta text-gris-500">
            El enlace sirve una sola vez y vence a los siete días. Al entrar, este teléfono queda con su sesión abierta hasta que la cierre o
            pasen treinta días sin usarla.
          </p>
        </form>
      )}
    </Tarjeta>
  )
}
